/* Product configurator, cards layout.

   The second of two views over the same rules. Everything that decides what is
   being bought lives in configurator-core.js. This file only binds and paints
   DOM.

   Unlike the summary view, this block owns the product form. That means the
   variant id input and the submit button are real descendants of the form, so
   the theme's product-form element can find them, and no input needs a form
   attribute. It also means Enter inside a text field would submit, which is
   guarded below. There is no sticky bar on this layout. */

(function () {
  'use strict';

  if (customElements.get('product-configurator-cards')) return;

  var core = window.PiriConfigurator;
  if (!core) {
    console.warn('Product configurator cards: configurator-core.js did not load.');
    return;
  }

  var money = core.money;

  /* Sibling section HTML, keyed by url, filled on hover or touch so the swap
     usually has its markup before the customer commits. Shared across
     instances because only one is ever live at a time. */
  var prefetched = {};

  /* Chosen files survive a swap because the page never unloads. A file input's
     value cannot be set from script, but its FileList can be replaced through
     DataTransfer, which is how they are put back afterwards. */
  var carriedPhotos = {};

  var SWAP_TIMEOUT = 8000;

  /* Set when the swap gives up and does a real navigation, so the destination
     knows the photos could not come with it. */
  var PHOTOS_LOST_KEY = 'piri:configurator:photoslost';

  function sectionUrl(href, sectionId) {
    var join = href.indexOf('?') === -1 ? '?' : '&';
    return href + join + 'section_id=' + encodeURIComponent(sectionId);
  }

  function fetchSection(href, sectionId) {
    var url = sectionUrl(href, sectionId);
    if (prefetched[url]) return prefetched[url];

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, SWAP_TIMEOUT);

    var request = fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        clearTimeout(timer);
        if (!response.ok) throw new Error('Section request failed: ' + response.status);
        return response.text();
      })
      .catch(function (error) {
        clearTimeout(timer);
        delete prefetched[url];
        throw error;
      });

    prefetched[url] = request;
    return request;
  }

  var CardsConfigurator = function () {
    return Reflect.construct(HTMLElement, [], CardsConfigurator);
  };
  CardsConfigurator.prototype = Object.create(HTMLElement.prototype);
  CardsConfigurator.prototype.constructor = CardsConfigurator;
  Object.setPrototypeOf(CardsConfigurator, HTMLElement);

  CardsConfigurator.prototype.connectedCallback = function () {
    var config = core.parseJSON(this.querySelector('[data-configurator-config]'));
    var product = core.parseJSON(this.querySelector('[data-configurator-product]'));

    if (!config || !config.pets || !product) {
      console.warn('Product configurator cards: missing or malformed custom.configurator metafield.');
      return;
    }

    this.engine = core.createEngine(config, product);
    if (!this.engine.isUsable()) {
      console.warn('Product configurator cards: the product needs Format and Size options.');
      return;
    }

    this.config = config;
    this.product = product;

    /* The section wrapper is what gets replaced on a pet count change. */
    this.section = this.closest('[data-section]');
    this.sectionId = this.section ? this.section.dataset.section : null;
    /* Which product this markup is for, so a history move knows whether a
       swap is actually needed. */
    this.productPath = '/products/' + (product.handle || '');

    /* The form is an ancestor here, not a lookup by id. */
    this.form = this.closest('form');
    this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
    this.submitBtn = this.form ? this.form.querySelector('[type="submit"]') : null;
    this.priceOut = this.form ? this.form.querySelector('[data-price]') : null;
    this.deliveryOut = this.form ? this.form.querySelector('[data-delivery]') : null;
    this.statusOut = this.querySelector('[data-status]');

    /* This view swaps the section in place, so photos carry across. A real
       navigation cannot restore them, and the page that navigated leaves a
       marker saying so. */
    var lostPhotos = false;
    try {
      lostPhotos = window.sessionStorage.getItem(PHOTOS_LOST_KEY) === '1';
      window.sessionStorage.removeItem(PHOTOS_LOST_KEY);
    } catch (e) {
      lostPhotos = false;
    }
    this.engine.photosSurvive = !lostPhotos && !!(this.section && typeof fetch !== 'undefined');

    /* The engine also reports what carried across, but this layout does not
       surface it. State simply arrives already applied. The jump flag is
       still needed, since it drives the mobile scroll on the fallback path. */
    var opening = this.engine.init();
    this.state = this.engine.state;

    var self = this;
    this.state.petNames.forEach(function (value, i) {
      var input = self.querySelector('[data-pet-name="' + (i + 1) + '"]');
      if (input && value) input.value = value;
    });

    this.bindEvents();
    this.engine.persist();
    this.render();

    /* Changing the pet count loads a different product. On mobile that would
       otherwise drop the customer at the top of a new page, so bring them
       back to the pet fields they were working in. */
    if (opening.jumped) this.scrollToPetsOnMobile();
  };

  /* After a pet count change on mobile, keep the customer looking at the
     control they just used rather than moving them down to the fields. */
  CardsConfigurator.prototype.scrollToPetsOnMobile = function () {
    if (!window.matchMedia || !window.matchMedia('(max-width: 767px)').matches) return;
    var pets = this.querySelector('[data-pet-count-section]');
    if (!pets) return;

    /* The position is computed and handed to window.scrollTo rather than
       using scrollIntoView, which some browsers ignore when asked to animate.
       The offset clears the theme's sticky header. Smooth scrolling is also
       ignored in some embedded browsers, so the plain two argument call is
       used as a fallback when nothing moved. */
    var go = function () {
      var header = document.querySelector('.header__wrapper, header, .site-header');
      var offset = header ? header.getBoundingClientRect().height : 0;
      var top = Math.max(0, pets.getBoundingClientRect().top + window.scrollY - offset - 16);
      var before = window.scrollY;

      try {
        window.scrollTo({ top: top, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, top);
        return;
      }

      setTimeout(function () {
        if (Math.abs(window.scrollY - before) < 2) window.scrollTo(0, top);
      }, 250);
    };

    /* Timers rather than requestAnimationFrame, which browsers throttle to a
       standstill while the tab is in the background. Run once the layout has
       settled, then again after images finish, since a late loading gallery
       shifts everything below it. */
    setTimeout(go, 0);
    setTimeout(go, 120);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () {
        setTimeout(go, 60);
      }, { once: true });
    }
  };

  CardsConfigurator.prototype.setState = function (patch) {
    this.engine.set(patch);
    this.render();
  };

  CardsConfigurator.prototype.showPhoto = function (index) {
    var input = this.querySelector('[data-pet-photo="' + index + '"]');
    if (!input) return;
    var field = input.parentNode;
    var slot = field.querySelector('[data-photo-slot]');
    var preview = field.querySelector('[data-photo-preview]');
    var image = field.querySelector('[data-photo-image]');
    var filename = field.querySelector('[data-photo-filename]');
    var file = input.files && input.files[0];

    if (!this.objectUrls) this.objectUrls = {};
    if (this.objectUrls[index]) {
      URL.revokeObjectURL(this.objectUrls[index]);
      delete this.objectUrls[index];
    }

    this.state.petPhotos[index - 1] = !!file;

    if (file) {
      var url = URL.createObjectURL(file);
      this.objectUrls[index] = url;
      if (image) {
        image.src = url;
        image.alt = 'Photo for pet ' + index + ', ' + file.name;
      }
      if (filename) filename.textContent = file.name;
      if (preview) preview.hidden = false;
      if (slot) slot.hidden = true;
    } else {
      if (image) {
        image.removeAttribute('src');
        image.alt = '';
      }
      if (filename) filename.textContent = '';
      if (preview) preview.hidden = true;
      if (slot) slot.hidden = false;
    }

    this.engine.persist();
    this.updateGate();
  };

  /* ---------- events ---------- */

  CardsConfigurator.prototype.bindEvents = function () {
    var self = this;

    this.addEventListener('click', function (event) {
      var target = event.target;

      var formatBtn = target.closest('[data-format-btn]');
      if (formatBtn) {
        self.setState({ format: formatBtn.dataset.formatBtn });
        return;
      }

      var sizeBtn = target.closest('[data-size-btn]');
      if (sizeBtn) {
        self.setState({ size: sizeBtn.dataset.sizeBtn });
        return;
      }

      /* Selecting the current frame again clears it, and the no frame swatch
         does the same explicitly. */
      var frameBtn = target.closest('[data-frame-btn]');
      if (frameBtn) {
        self.setState({ frame: frameBtn.dataset.frameBtn });
        return;
      }

      var backgroundBtn = target.closest('[data-background-btn]');
      if (backgroundBtn) {
        self.setState({ background: backgroundBtn.dataset.backgroundBtn });
        return;
      }

      var sibling = target.closest('[data-sibling]');
      if (sibling) {
        event.preventDefault();
        self.goToSibling(sibling.getAttribute('href'));
        return;
      }

      var opener = target.closest('[data-open-dialog]');
      if (opener) {
        var dialog = self.querySelector('[data-dialog="' + opener.dataset.openDialog + '"]');
        if (dialog && dialog.showModal) dialog.showModal();
        return;
      }

      var closer = target.closest('[data-close-dialog]');
      if (closer) {
        var openDialog = closer.closest('dialog');
        if (openDialog) openDialog.close();
        return;
      }

      var remove = target.closest('[data-photo-remove]');
      if (remove) {
        var index = parseInt(remove.dataset.photoRemove, 10);
        var photoInput = self.querySelector('[data-pet-photo="' + index + '"]');
        if (photoInput) photoInput.value = '';
        self.showPhoto(index);
        return;
      }
    });

    this.addEventListener('change', function (event) {
      var input = event.target.closest('[data-pet-photo]');
      if (!input) return;
      var index = parseInt(input.dataset.petPhoto, 10);
      self.clearPhotoError(index);
      self.showPhoto(index);
    });

    /* Add to cart stays clickable with a photo missing. Catching the submit
       on the way down means this runs before the theme's own handler, so the
       customer is shown which photo is missing instead of a dead button.

       The sticky bar, when a template leaves it on, has its own form that
       knows nothing about the photos or the line item properties. Its submit
       is redirected through this form so every route to the cart carries the
       same payload and the same check. */
    document.addEventListener('submit', function (event) {
      var target = event.target;
      if (!target || target.tagName !== 'FORM') return;

      var isOurs = target === self.form;
      var isSticky = !isOurs && !!target.closest('sticky-add-cart');
      if (!isOurs && !isSticky) return;

      var missing = self.missingPhotoIndexes();

      if (isSticky) {
        event.preventDefault();
        event.stopPropagation();
        if (missing.length) {
          self.flagMissingPhotos(missing);
        } else if (self.form && self.form.requestSubmit) {
          self.form.requestSubmit();
        } else if (self.submitBtn) {
          self.submitBtn.click();
        }
        return;
      }

      if (missing.length) {
        event.preventDefault();
        event.stopPropagation();
        self.flagMissingPhotos(missing);
        return;
      }

      /* Nothing is missing, so this add is going ahead. Take the request over
         so the photo upload can report progress, which fetch cannot do. */
      event.preventDefault();
      event.stopPropagation();
      self.submitWithProgress();
    }, true);

    Array.prototype.forEach.call(this.querySelectorAll('dialog[data-dialog]'), function (dialog) {
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.preventDefault();
          dialog.close();
        }
      });
    });

    this.addEventListener('input', function (event) {
      var input = event.target.closest('[data-pet-name]');
      if (!input) return;
      var index = parseInt(input.dataset.petName, 10) - 1;
      self.state.petNames[index] = input.value;
      self.engine.persist();
    });

    /* The name fields now live inside a real form, so Enter would submit and
       add to cart from a keystroke meant to finish typing. */
    this.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      if (!event.target.closest('[data-pet-name]')) return;
      event.preventDefault();
    });

    /* Warm the cache as soon as the customer shows intent, so the swap has its
       markup ready by the time they commit. */
    var warm = function (event) {
      var link = event.target.closest && event.target.closest('[data-sibling]');
      if (!link || !self.sectionId) return;
      fetchSection(link.getAttribute('href'), self.sectionId).catch(function () {
        /* A failed prefetch is not worth reporting. The click falls back. */
      });
    };
    this.addEventListener('pointerenter', warm, true);
    this.addEventListener('touchstart', warm, { passive: true });
    this.addEventListener('focusin', warm);

    /* The back button should return to the previous pet count rather than
       leaving the customer on markup that no longer matches the address.
       The entry we started on carries no state of ours, so the target is
       taken from the address rather than from the state object. */
    if (!window.__piriPopstateBound) {
      window.__piriPopstateBound = true;
      window.addEventListener('popstate', function () {
        var live = document.querySelector('product-configurator-cards');
        if (!live) return;
        var path = window.location.pathname;
        if (path.indexOf('/products/') !== 0) return;
        /* Already showing it, nothing to swap. */
        if (live.productPath === path) return;
        live.goToSibling(path, true);
      });
    }
  };

  /* ---------- sibling swap ---------- */

  /* Replace the product section in place rather than navigating. The page
     never unloads, so scroll position, the rest of the document and the
     already attached photos all survive. Any failure falls back to a normal
     navigation, which is the behaviour this replaced. */
  CardsConfigurator.prototype.goToSibling = function (href, fromHistory) {
    var self = this;

    if (!this.section || !this.sectionId || typeof fetch === 'undefined') {
      this.navigateAway(href);
      return;
    }

    this.engine.persist();
    /* Set for the fallback path, where the destination is a fresh page load
       and needs to know a jump happened. */
    core.setJumpFlag();
    this.rememberPhotos();
    this.section.classList.add('pcc-section--loading');

    fetchSection(href, this.sectionId)
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        /* A section render returns the section wrapper itself, not a page. */
        var incoming = doc.querySelector('[data-section]') || doc.body.firstElementChild;
        if (!incoming) throw new Error('Section markup not found in response');

        self.section.innerHTML = incoming.innerHTML;

        var url = self.engine.siblingHref(href);
        if (!fromHistory && window.history && window.history.pushState) {
          window.history.pushState({ piriSwap: href }, '', url);
        }

        /* Custom elements upgrade themselves on insertion. This tells the rest
           of the theme to re-run its per section setup. */
        document.dispatchEvent(new CustomEvent('shopify:section:load', {
          detail: { sectionId: self.sectionId },
          bubbles: true
        }));

        /* The new instance restores the photos and finishes the handover. */
        var fresh = document.querySelector('product-configurator-cards');
        if (fresh && fresh !== self) {
          fresh.adoptCarriedPhotos();
          fresh.reportPageView(self.config.pets);
        }
      })
      .catch(function (error) {
        console.warn('Product configurator cards: swap failed, loading the page instead.', error);
        self.navigateAway(href);
      })
      .then(function () {
        if (self.section) self.section.classList.remove('pcc-section--loading');
      });
  };

  CardsConfigurator.prototype.navigateAway = function (href) {
    this.engine.persist();
    core.setJumpFlag();
    try {
      if (Object.keys(carriedPhotos).length) {
        window.sessionStorage.setItem(PHOTOS_LOST_KEY, '1');
      }
    } catch (e) {
      /* Nothing to do. The notice simply keeps its optimistic wording. */
    }
    window.location.assign(this.engine.siblingHref(href));
  };

  /* ---------- add to cart with upload progress ---------- */

  /* The theme adds to cart with fetch, which cannot report upload progress.
     Photos are the only large part of this payload and can be several
     megabytes from a phone, so the request is sent with XMLHttpRequest
     instead and the button fills as the bytes go up.

     Everything after the response mirrors what the theme's own product-form
     does: swap the mini cart sections, refresh the counts, open the cart. */
  CardsConfigurator.prototype.submitWithProgress = function () {
    var self = this;
    var form = this.form;
    if (!form) return;

    var button = this.submitBtn;
    var label = form.querySelector('[data-submit-label]');
    var labelText = label ? label.textContent : '';
    var progress = form.querySelector('[data-upload-progress]');
    var bar = form.querySelector('[data-upload-bar]');
    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    var setProgress = function (ratio) {
      if (!progress || !bar) return;
      progress.hidden = false;
      bar.style.width = Math.round(ratio * 100) + '%';
    };

    var finish = function () {
      if (button) {
        button.disabled = false;
        button.setAttribute('aria-disabled', 'false');
        button.classList.remove('loading');
      }
      if (label) label.textContent = labelText;
      if (progress) progress.hidden = true;
      if (bar) bar.style.width = '0%';
    };

    var fail = function (message) {
      finish();
      if (self.statusOut) {
        self.statusOut.textContent = message || 'Something went wrong. Please try again.';
        self.statusOut.hidden = false;
      }
    };

    var formData = new FormData(form);
    if (cart) {
      try {
        formData.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      } catch (e) {
        /* Without the sections the add still works, the mini cart just will
           not repaint, so the page falls back to a cart visit below. */
      }
    }

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
    }
    if (self.statusOut) self.statusOut.hidden = true;
    setProgress(0);

    var xhr = new XMLHttpRequest();
    xhr.open('POST', (window.routes && window.routes.cart_add_url) || '/cart/add.js', true);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.setRequestHeader('Accept', 'application/javascript');

    xhr.upload.addEventListener('progress', function (event) {
      if (!event.lengthComputable) return;
      setProgress(event.loaded / event.total);
      /* Once the bytes are gone the wait is Shopify's, not the network's. */
      if (label && event.loaded >= event.total) label.textContent = 'Adding to cart';
    });

    xhr.addEventListener('load', function () {
      var response;
      try {
        response = JSON.parse(xhr.responseText);
      } catch (e) {
        fail();
        return;
      }

      if (xhr.status >= 400 || response.status) {
        fail(response.description || response.message);
        return;
      }

      /* Repaint the mini cart from the sections the response carries. */
      try {
        if (cart && response.sections) {
          cart.getSectionsToRender().forEach(function (section) {
            var target = document.getElementById(section.id);
            if (!target || !response.sections[section.id]) return;
            var parsed = new DOMParser().parseFromString(response.sections[section.id], 'text/html');
            var inner = parsed.querySelector('#minicart-form');
            if (inner) target.innerHTML = inner.innerHTML;
          });
          cart.cartAction();
          cart.classList.remove('is-empty');
          if (typeof showMinicartAddSuccessMessage === 'function') {
            showMinicartAddSuccessMessage(cart);
          }
          cart.open();
        } else if (!cart) {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }
      } catch (e) {
        console.warn('Product configurator cards: cart did not repaint.', e);
      }

      /* Header count and totals, the same refresh the theme does. */
      fetch('/cart.json')
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.item_count === undefined) return;
          document.querySelectorAll('.cart-count').forEach(function (el) {
            el.innerHTML = el.classList.contains('cart-count-drawer')
              ? '(' + data.item_count + ')'
              : (data.item_count > 100 ? '~' : data.item_count);
          });
          var total = document.querySelector('header-total-price');
          if (total && total.updateTotal) total.updateTotal(data);
          var freeShip = document.querySelector('free-ship-progress-bar');
          if (freeShip && freeShip.init) freeShip.init(data.items_subtotal_price);
        })
        .catch(function () {
          /* The cart is correct even if the header badge lags a moment. */
        });

      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response
          });
        }
      } catch (e) {
        /* Nothing downstream of this is required for the add to succeed. */
      }

      finish();
    });

    xhr.addEventListener('error', function () { fail(); });
    xhr.addEventListener('abort', function () { finish(); });

    xhr.send(formData);
  };

  /* A swap is a product change with no page load, so nothing would otherwise
     tell Shopify the customer moved. Without this, two and three pet can take
     orders while showing almost no views, which makes their conversion rate
     meaningless.

     These are Shopify internals rather than a documented API, so every access
     is guarded. If one is renamed the reporting degrades and the page carries
     on working. */
  CardsConfigurator.prototype.reportPageView = function (previousPets) {
    try {
      var analytics = window.ShopifyAnalytics;
      var variant = this.currentVariant || this.engine.resolveVariant();

      if (analytics && analytics.meta) {
        var meta = analytics.meta;

        if (meta.page) {
          meta.page.resourceId = this.product.id;
          meta.page.resourceType = 'product';
          meta.page.pageType = 'product';
        }

        /* Rebuilt from the product JSON the block already serialises, in the
           shape trekkie expects. */
        meta.product = {
          id: this.product.id,
          gid: 'gid://shopify/Product/' + this.product.id,
          vendor: this.product.vendor,
          type: this.product.type,
          handle: this.product.handle,
          variants: (this.product.variants || []).map(function (v) {
            return {
              id: v.id,
              price: v.price,
              name: v.name,
              public_title: v.public_title,
              sku: v.sku
            };
          })
        };

        if (variant) meta.selectedVariantId = String(variant.id);
      }

      /* This is the call Shopify's own reports count. */
      if (analytics && analytics.lib && typeof analytics.lib.page === 'function') {
        analytics.lib.page();
      }

      /* A custom event as well, so web pixels and any future GA4 or Meta setup
         have a hook without another code change. */
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('pet_count_changed', {
          from_pets: previousPets,
          to_pets: this.config.pets,
          handle: this.product.handle,
          product_id: this.product.id,
          variant_id: variant ? variant.id : null
        });
      }
    } catch (error) {
      console.warn('Product configurator cards: could not report the page view.', error);
    }
  };

  /* Hold the chosen files so they can be written into the next markup. */
  CardsConfigurator.prototype.rememberPhotos = function () {
    carriedPhotos = {};
    for (var i = 1; i <= this.config.pets; i++) {
      var input = this.querySelector('[data-pet-photo="' + i + '"]');
      if (input && input.files && input.files.length) {
        carriedPhotos[i] = input.files[0];
      }
    }
  };

  /* Put them back. A file input's value cannot be assigned, but its FileList
     can be replaced through DataTransfer. Pets beyond the new count are simply
     dropped, and a new pet starts empty. */
  CardsConfigurator.prototype.adoptCarriedPhotos = function () {
    var self = this;
    var restored = false;

    Object.keys(carriedPhotos).forEach(function (key) {
      var index = parseInt(key, 10);
      if (index > self.config.pets) return;
      var input = self.querySelector('[data-pet-photo="' + index + '"]');
      if (!input || typeof DataTransfer === 'undefined') return;
      try {
        var data = new DataTransfer();
        data.items.add(carriedPhotos[key]);
        input.files = data.files;
        self.showPhoto(index);
        restored = true;
      } catch (e) {
        /* Older browsers cannot rebuild a FileList. The slot stays empty and
           the gate asks for the photo again, which is the old behaviour. */
      }
    });

    carriedPhotos = {};
    if (restored) this.render();
    this.scrollToPetsOnMobile();
  };

  /* ---------- rendering ---------- */

  CardsConfigurator.prototype.render = function () {
    var state = this.state;
    var engine = this.engine;
    var self = this;

    var pressed = function (selector, isOn) {
      var el = self.querySelector(selector);
      if (el) el.setAttribute('aria-pressed', isOn ? 'true' : 'false');
    };

    pressed('[data-format-btn="digital"]', state.format === 'digital');
    pressed('[data-format-btn="print"]', state.format === 'print');

    var printOptions = this.querySelector('[data-print-options]');
    if (printOptions) printOptions.hidden = state.format === 'digital';
    var frameSection = this.querySelector('[data-frame-section]');
    if (frameSection) frameSection.hidden = state.format === 'digital';

    /* The paper size is carried on the button rather than mapped again here,
       so a rename of the option values only has to be handled in one place. */
    var paper = '';
    Array.prototype.forEach.call(this.querySelectorAll('[data-size-btn]'), function (btn) {
      var on = btn.dataset.sizeBtn === state.size;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) paper = btn.dataset.paper || '';
    });
    Array.prototype.forEach.call(this.querySelectorAll('[data-frame-btn]'), function (btn) {
      btn.setAttribute('aria-pressed', btn.dataset.frameBtn === state.frame ? 'true' : 'false');
    });
    Array.prototype.forEach.call(this.querySelectorAll('[data-background-btn]'), function (btn) {
      btn.setAttribute('aria-pressed', btn.dataset.backgroundBtn === state.background ? 'true' : 'false');
    });

    this.paper = paper;

    /* Each heading names the current choice and what it adds over the
       cheapest one, both read from the variants rather than written here, so
       an admin price edit flows through. The base size adds nothing, so it
       carries no figure. */
    var sizeCurrent = this.querySelector('[data-size-current]');
    if (sizeCurrent) {
      var sizeDelta = engine.sizeDeltaCents(state.size);
      sizeCurrent.textContent = '· ' + state.size + (sizeDelta > 0 ? ' +' + money(sizeDelta) : '');
    }

    var frameCurrent = this.querySelector('[data-frame-current]');
    if (frameCurrent) {
      if (state.frame === 'none') {
        frameCurrent.textContent = '· Print only, no frames';
      } else {
        var frameDelta = engine.frameAddCents(state.size);
        frameCurrent.textContent = '· ' + state.frame + (frameDelta > 0 ? ' +' + money(frameDelta) : '');
      }
    }

    var backgroundCurrent = this.querySelector('[data-background-current]');
    if (backgroundCurrent) backgroundCurrent.textContent = '· ' + state.background;

    var backgroundProp = this.querySelector('[data-property-background]');
    if (backgroundProp) backgroundProp.value = state.background;

    /* The printed option shows the price of the current print selection, so
       the panel and the footer never disagree. */
    var printPrice = this.form && this.form.querySelector('[data-print-price]');
    var printCaption = this.form && this.form.querySelector('[data-print-caption]');
    var printVariant = engine.variantFor(
      core.FRAME_TO_FORMAT[state.frame] || 'Print only',
      state.size,
      state.background
    );
    if (printPrice && printVariant) printPrice.textContent = money(printVariant.price);
    if (printCaption) {
      printCaption.textContent = paper ? paper + ', free shipping' : 'free shipping';
    }

    this.applyVariant();
    this.togglePrintSections();
    this.updateGate();
  };

  CardsConfigurator.prototype.applyVariant = function () {
    var variant = this.engine.resolveVariant();
    this.currentVariant = variant;

    if (!variant) {
      if (this.idInput) this.idInput.value = '';
      return;
    }

    if (this.idInput) {
      this.idInput.value = variant.id;
      this.idInput.disabled = false;
    }

    if (this.priceOut) this.priceOut.textContent = money(variant.price);
    this.syncAfterpay(variant.price);
    if (this.deliveryOut) {
      this.deliveryOut.textContent = this.state.format === 'digital'
        ? 'digital file, delivered by email'
        : (this.paper ? this.paper + ' · free shipping' : 'free shipping');
    }

    this.syncGallery(variant);
    this.syncStickyBar(variant);
    this.engine.syncUrl(variant.id);
  };

  /* The Afterpay messaging block only re-reads its amount when the theme
     re-renders the section for a variant change. This block swaps variants
     itself, so without this the instalment text keeps the first price. */
  CardsConfigurator.prototype.syncAfterpay = function (cents) {
    var amount = String(cents / 100);
    document.querySelectorAll('square-placement').forEach(function (el) {
      if (el.getAttribute('data-amount') !== amount) el.setAttribute('data-amount', amount);
    });
  };

  /* This block replaces the theme's variant picker, so nothing is left to tell
     the gallery a variant changed. The theme's own updateMedia is not reachable
     here: it hangs off a picker whose select never matches this state, and it
     reads a productTarget only assigned inside its change handler. Moving the
     gallery directly is both shorter and honest about what is happening.

     Only the format family and the background change the picture. Size does
     not, and neither does the frame finish, since all four finishes share one
     image per background. Both fall out of this for free, because a variant
     that shares its image simply resolves to the slide already showing. */
  CardsConfigurator.prototype.syncGallery = function (variant) {
    if (!variant.featured_media || !this.sectionId) return;

    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    var mediaId = this.sectionId + '-' + variant.featured_media.id;

    if (gallery.dataset.layout === 'thumbnail') {
      var swiper = gallery.querySelector('slide-with-thumbs');
      if (!swiper || typeof swiper.functionGoto !== 'function') return;
      var slide = gallery.querySelector(
        '.swiper-wrapper-preview [data-media-id="' + mediaId + '"]'
      );
      if (!slide) return;
      var position = parseInt(slide.getAttribute('data-position'), 10);
      if (!isNaN(position)) swiper.functionGoto(position - 1);
      return;
    }

    /* Stacked layouts have no slider to drive, so the matching image is
       scrolled to instead, the same way the theme handles them. */
    var target = gallery.querySelector('[data-media-id="' + mediaId + '"]');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  /* The sticky bar carries its own thumbnail and price, fed by the same picker
     that never hears about a change, so both go stale without this. */
  CardsConfigurator.prototype.syncStickyBar = function (variant) {
    var sticky = document.querySelector('sticky-add-cart');
    if (!sticky) return;

    if (variant.featured_media && variant.featured_media.preview_image) {
      var image = sticky.querySelector('img');
      if (image) {
        image.src = variant.featured_media.preview_image.src;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      }
    }

    var stickyId = sticky.querySelector('[name="id"]');
    if (stickyId) stickyId.value = variant.id;
  };

  CardsConfigurator.prototype.togglePrintSections = function () {
    var digital = this.state.format === 'digital';
    Array.prototype.forEach.call(document.querySelectorAll('[data-print-only-section]'), function (el) {
      el.hidden = digital;
    });
  };

  /* Every pet still without a photo. Names are optional and are never
     checked, only the photos are required. */
  CardsConfigurator.prototype.missingPhotoIndexes = function () {
    var missing = [];
    for (var i = 1; i <= this.config.pets; i++) {
      var input = this.querySelector('[data-pet-photo="' + i + '"]');
      if (!input) continue;
      if (!input.files || !input.files.length) missing.push(i);
    }
    return missing;
  };

  CardsConfigurator.prototype.missingPhotoIndex = function () {
    var missing = this.missingPhotoIndexes();
    return missing.length ? missing[0] : 0;
  };

  CardsConfigurator.prototype.missingPhotoLabel = function () {
    var index = this.missingPhotoIndex();
    if (!index) return null;
    var input = this.querySelector('[data-pet-photo="' + index + '"]');
    return (input && input.dataset.petLabel) || 'pet ' + index;
  };

  /* Mark every photo still missing, then bring the first one into view. Used
     when the customer tries to add to cart, rather than disabling the button
     and leaving them to work out why. Flagging all of them means a three pet
     order shows all three gaps at once instead of one per attempt. */
  CardsConfigurator.prototype.flagMissingPhotos = function (indexes) {
    var self = this;
    var template = this.dataset.photoMessage || 'Add a photo for [pet] to continue.';
    var first = null;

    indexes.forEach(function (index) {
      var input = self.querySelector('[data-pet-photo="' + index + '"]');
      if (!input) return;
      var field = input.parentNode;
      var slot = field.querySelector('[data-photo-slot]');
      var error = field.querySelector('[data-photo-error]');

      if (slot) slot.classList.add('pcc__photo--error');
      if (error) {
        error.textContent = template.replace('[pet]', input.dataset.petLabel || 'this pet');
        error.hidden = false;
      }
      if (!first) first = field;
    });

    if (!first) return;
    var target = first.closest('.pcc__pet') || first;
    if (target.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    /* Focus the first control so keyboard users land on it too, without the
       browser scrolling a second time. */
    var label = first.querySelector('[data-photo-slot]');
    if (label && label.focus) label.focus({ preventScroll: true });
  };

  CardsConfigurator.prototype.clearPhotoError = function (index) {
    var input = this.querySelector('[data-pet-photo="' + index + '"]');
    if (!input) return;
    var field = input.parentNode;
    var slot = field.querySelector('[data-photo-slot]');
    var error = field.querySelector('[data-photo-error]');
    if (slot) slot.classList.remove('pcc__photo--error');
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  };

  /* The button is never disabled. A combination that cannot be bought is the
     only thing that blocks it, and that is reported in the status line. */
  CardsConfigurator.prototype.updateGate = function () {
    var variant = this.currentVariant;
    var message = '';
    if (!variant) {
      message = 'That combination is not available. Try a different option.';
    } else if (variant.available === false) {
      message = 'This combination is sold out.';
    }

    if (this.submitBtn) {
      this.submitBtn.disabled = !!message;
      this.submitBtn.setAttribute('aria-disabled', message ? 'true' : 'false');
    }

    if (this.statusOut) {
      this.statusOut.textContent = message;
      this.statusOut.hidden = !message;
    }
  };

  customElements.define('product-configurator-cards', CardsConfigurator);
})();
